import { useEffect, useMemo, useRef, useState } from 'react'
import { Head, Link, router } from '@inertiajs/react'

import { ShareButton } from '@/components/changelog/share-button'
import { ChangelogPageShell } from '@/components/changelog/changelog-page-shell'
import { TrendsDrawer } from '@/components/trends/trends-drawer'
import { Button } from '@/components/ui/button'
import { publicChangelogOwnerPath, publicChangelogRepositoryPath } from '@/routes'
import type { ChangelogEntry } from '@/types/changelog'
import type { PublicChangelogsRepositoryShow } from '@/types'

type Props = PublicChangelogsRepositoryShow & {
  entries: ChangelogEntry[]
  groupedEntries: Record<string, ChangelogEntry[]>
}

function GitHubMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" className={className ?? 'size-4 fill-current'} aria-hidden>
      <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0 0 16 8c0-4.42-3.58-8-8-8z" />
    </svg>
  )
}

function groupChangelogByMonth(entries: ChangelogEntry[]) {
  return entries.reduce<Record<string, ChangelogEntry[]>>((grouped, entry) => {
    const monthKey = new Date(entry.timestamp).toLocaleString('en-US', { month: 'long', year: 'numeric' })
    grouped[monthKey] ??= []
    grouped[monthKey].push(entry)
    return grouped
  }, {})
}

function formatRelativeTime(timestamp: string) {
  const deltaSeconds = Math.round((Date.parse(timestamp) - Date.now()) / 1000)
  const formatter = new Intl.RelativeTimeFormat('en', { numeric: 'auto' })
  const divisions: Array<[number, Intl.RelativeTimeFormatUnit]> = [
    [60, 'second'],
    [60, 'minute'],
    [24, 'hour'],
    [7, 'day'],
    [4.34524, 'week'],
    [12, 'month'],
    [Number.POSITIVE_INFINITY, 'year'],
  ]

  let duration = deltaSeconds
  for (const [amount, unit] of divisions) {
    if (Math.abs(duration) < amount) {
      return formatter.format(duration, unit)
    }
    duration = Math.round(duration / amount)
  }

  return 'Awaiting first update'
}

export default function PublicChangelogsRepositoryShow({
  repository,
  entries: initialEntries,
  nextCursor: initialNextCursor,
  commitsProcessed,
  totalChangesInLastWeek,
  trends,
}: Props) {
  const [entries, setEntries] = useState(initialEntries)
  const [nextCursor, setNextCursor] = useState(initialNextCursor)
  const [loadingMore, setLoadingMore] = useState(false)
  const sentinelRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    setEntries(initialEntries)
    setNextCursor(initialNextCursor)
  }, [initialEntries, initialNextCursor])

  const groupedEntries = useMemo(() => groupChangelogByMonth(entries), [entries])
  const latestUpdate = entries[0]?.timestamp ? new Date(entries[0].timestamp) : null
  const lastUpdatedText = latestUpdate ? formatRelativeTime(entries[0].timestamp) : 'Awaiting first update'
  const description = `Public changelog for ${repository.fullName}: ${totalChangesInLastWeek.toLocaleString()} code changes in the last 7 days, summarized by Git Digest for engineering teams.`

  useEffect(() => {
    if (!nextCursor || loadingMore) return

    const node = sentinelRef.current
    if (!node) return

    const observer = new IntersectionObserver(
      (records) => {
        if (!records.some((record) => record.isIntersecting)) return

        setLoadingMore(true)
        router.get(
          publicChangelogRepositoryPath(repository.owner, repository.name, { cursor: nextCursor }),
          {},
          {
            preserveScroll: true,
            preserveState: true,
            only: ['entries', 'groupedEntries', 'nextCursor'],
            onSuccess: (page) => {
              const props = page.props as unknown as Pick<Props, 'entries' | 'nextCursor'>
              setEntries((current) => [...current, ...props.entries])
              setNextCursor(props.nextCursor)
            },
            onFinish: () => setLoadingMore(false),
          },
        )
      },
      { rootMargin: '240px' },
    )

    observer.observe(node)
    return () => observer.disconnect()
  }, [loadingMore, nextCursor, repository.name, repository.owner])

  const hero = (
    <>
      <nav className="text-muted-foreground mb-8 text-sm">
        <Link href="/" className="hover:text-foreground">
          Home
        </Link>
        <span className="mx-2">/</span>
        <Link href="/changelog" className="hover:text-foreground">
          Changelog
        </Link>
        <span className="mx-2">/</span>
        <Link href={publicChangelogOwnerPath(repository.owner)} className="hover:text-foreground">
          {repository.owner}
        </Link>
        <span className="mx-2">/</span>
        <span className="text-foreground">{repository.name}</span>
      </nav>

      <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex-1">
          <div className="text-muted-foreground mb-6 flex flex-wrap items-center gap-4 text-sm">
            <span className="flex items-center gap-2 tracking-wide uppercase">
              <GitHubMark />
              <span>{repository.fullName}</span>
            </span>
            <TrendsDrawer trends={trends} />
          </div>

          <h1 className="from-foreground to-muted-foreground mb-6 bg-gradient-to-r bg-clip-text text-4xl text-transparent sm:text-5xl">
            What&apos;s new in {repository.name}
          </h1>

          <div className="text-muted-foreground mt-8 flex flex-col gap-4 text-sm sm:flex-row sm:items-center sm:gap-8">
            <span className="flex items-center gap-2">
              <span className="text-xl">🤖</span>
              <span>Processed {commitsProcessed.toLocaleString()} code changes</span>
            </span>
            <span className="flex items-center gap-2">
              <span className="text-xl">🌱</span>
              <span>
                Default branch
                <code className="bg-muted ml-2 rounded px-2 py-1 font-mono text-xs">{repository.defaultBranch}</code>
              </span>
            </span>
            <span className="flex items-center gap-2">
              <span className="text-xl">🕒</span>
              <span>Last updated {lastUpdatedText}</span>
            </span>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          {repository.isPublic ? (
            <Button asChild size="sm" variant="ghost" className="text-muted-foreground hover:text-foreground">
              <a href={repository.url} target="_blank" rel="noopener noreferrer">
                <GitHubMark className="mr-2 h-4 w-4" />
                View on GitHub
              </a>
            </Button>
          ) : null}
          <ShareButton variant="outline" />
        </div>
      </div>
    </>
  )

  return (
    <>
      <Head title={repository.fullName}>
        <meta head-key="description" name="description" content={description} />
      </Head>
      <ChangelogPageShell
        hero={hero}
        entries={entries}
        groupedEntries={groupedEntries}
        emptyStateMessage={`No changelog entries yet for ${repository.fullName}. Check back soon for updates!`}
        footer={
          nextCursor ? (
            <div ref={sentinelRef} className="text-muted-foreground py-8 text-center text-sm">
              {loadingMore ? 'Loading more updates…' : 'Scroll for more updates'}
            </div>
          ) : undefined
        }
      />
    </>
  )
}
